import { useEffect, useMemo, useState } from 'react'
import { Box, Alert, LinearProgress, Divider, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import _ from 'lodash'
import { useFarmId } from '~/common/use-farm-id'
import { useOrgParams } from '~/common/use-org-params'
import { supabaseGetter } from '~/api/supabase-getter'
import { useOrganizations } from '~/common/use-organizations'
import { setActiveOrgs, useOrgContextDispatch } from '~/common/context/organization/org-context'
import { OrgBreadcrumbs } from '~/dashboard/org-breadcrumbs'
import { useCurrentUser } from '~/common/use-current-user'
import { useCropData } from '~/crop/crop-list/use-crop-data'
import {
  APPBAR_HEIGHT_REM,
  APPBAR_EXTRA_DESKTOP_REM,
  CONTENT_PADDING_RBL_REM,
  CONTENT_PADDING_T_REM,
  DASHBOARD_TITLE_HEIGHT_REM,
  FULLSCREEN_HEIGHT_MD,
} from '~/common/constants/layout-constants'
import { GddChart } from './climate/gdd-chart'
import { TaskVelocityChart } from './org-metrics/task-velocity-chart'
import { CropPhaseChart } from './org-metrics/crop-phase-chart'
import { NumericTotals } from './org-metrics/numeric-totals'
import { CropHarvestTable } from './org-metrics/crop-harvest-table'

// GDD base label respecting temperature unit
const gddBaseLabel = (base, isCelsius) =>
  `GDD ${base === '5' ? (isCelsius ? '5' : '41') : (isCelsius ? '10' : '50')}`

const SectionLabel = ({ children }) => (
  <Typography
    variant="caption"
    sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.65rem', letterSpacing: '0.05em', flexShrink: 0, mb: 0.25, display: 'block' }}
  >
    {children}
  </Typography>
)

// Inner component — has access to OrgContextDispatch for breadcrumb org setup
const MetricsFullscreenInner = () => {
  const dispatch = useOrgContextDispatch()
  const { farmId, plotId, patchId } = useOrgParams()
  const { userPreferences } = useCurrentUser()
  const tempUnit = userPreferences?.temperature_unit || '°C'
  const isCelsius = tempUnit === '°C'
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'))

  const { crops } = useCropData()

  const [climate, setClimate] = useState(null)
  useEffect(() => {
    if (!farmId) return
    supabaseGetter.climates.getByOrganization(farmId).then(({ data }) => {
      setClimate(data ?? null)
    })
  }, [farmId])

  const gdd5Series = climate?.gdd5_series ?? null
  const gdd10Series = climate?.gdd10_series ?? null
  const lfd = climate?.avg_lfd ? Math.round(climate.avg_lfd) : null
  const ffd = climate?.avg_ffd ? Math.round(climate.avg_ffd) : null

  const ids = useMemo(() => _.compact([farmId, plotId, patchId]), [farmId, plotId, patchId])
  const typeValidations = useMemo(() => {
    if (plotId) return patchId ? ['farm', 'plot', 'patch'] : ['farm', 'plot']
    return ['farm']
  }, [plotId, patchId])

  const { data: organizations } = useOrganizations({ ids, typeValidations })

  const orderedOrgs = useMemo(() => {
    if (!organizations) return []
    return ids.map(id => organizations.find(org => org.id === id)).filter(Boolean)
  }, [organizations, ids])

  useEffect(() => {
    dispatch(setActiveOrgs({ activeOrgs: orderedOrgs }))
  }, [dispatch, orderedOrgs])

  return (
    <Box
      sx={{
        height: {
          xs: `calc(100vh - ${APPBAR_HEIGHT_REM} + ${APPBAR_EXTRA_DESKTOP_REM})`,
          md: `calc(100vh - ${APPBAR_HEIGHT_REM})`,
          lg: FULLSCREEN_HEIGHT_MD,
        },
        overflow: 'hidden',
        m: `-${CONTENT_PADDING_T_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM} -${CONTENT_PADDING_RBL_REM}`,
        pt: CONTENT_PADDING_T_REM,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Breadcrumbs header — matches dashboard placement */}
      <Box
        sx={{
          display: 'flex',
          height: DASHBOARD_TITLE_HEIGHT_REM,
          mb: '0.25rem',
          pt: 1,
          flexShrink: 0,
          px: CONTENT_PADDING_RBL_REM,
        }}
      >
        <OrgBreadcrumbs baseRoute="metrics" />
      </Box>

      {/* Chart area */}
      <Box sx={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {isDesktop ? (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'row', overflow: 'hidden' }}>
            {/* Left column (~40%): GDD5 stacked above GDD10 */}
            <Box sx={{ flex: '2 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', px: 1, pb: 1 }}>
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SectionLabel>SEASON PROGRESS · <Box component="span" sx={{ fontWeight: 800 }}>{gddBaseLabel('5', isCelsius)}</Box></SectionLabel>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.5, flexShrink: 0 }}>
                  Solar battery drain for warm (10) and cool (5) season crops
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <GddChart fullscreen gddBase="5" tempUnit={tempUnit} gdd5Series={gdd5Series} gdd10Series={gdd10Series} lfd={lfd} ffd={ffd} />
                </Box>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <SectionLabel>SEASON PROGRESS · <Box component="span" sx={{ fontWeight: 800 }}>{gddBaseLabel('10', isCelsius)}</Box></SectionLabel>
                <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.5, flexShrink: 0 }}>
                  Solar battery drain for warm (10) and cool (5) season crops
                </Typography>
                <Box sx={{ flex: 1, minHeight: 0 }}>
                  <GddChart fullscreen gddBase="10" tempUnit={tempUnit} gdd5Series={gdd5Series} gdd10Series={gdd10Series} lfd={lfd} ffd={ffd} />
                </Box>
              </Box>
            </Box>

            <Divider orientation="vertical" flexItem />

            {/* Right column (~60%): harvest table on top, charts row below */}
            <Box sx={{ flex: '3 1 0', minWidth: 0, display: 'flex', flexDirection: 'column', px: 1, pb: 1 }}>
              <CropHarvestTable crops={crops} />

              <Divider sx={{ my: 1 }} />

              {/* Bottom row: task velocity | crop phase | numeric totals */}
              <Box sx={{ flex: 1, minHeight: 120, display: 'flex', flexDirection: 'row', gap: 1 }}>
                <Box sx={{ flex: '2 1 0', minWidth: 0, minHeight: 0 }}>
                  <TaskVelocityChart />
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ flex: '2 1 0', minWidth: 0, minHeight: 0 }}>
                  <CropPhaseChart />
                </Box>
                <Divider orientation="vertical" flexItem />
                <Box sx={{ flex: '1 1 0', minWidth: 0, minHeight: 0 }}>
                  <NumericTotals />
                </Box>
              </Box>
            </Box>
          </Box>
        ) : (
          <Stack divider={<Divider />} spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', px: 1, pb: 2 }}>
            <Box>
              <SectionLabel>SEASON PROGRESS · <Box component="span" sx={{ fontWeight: 800 }}>{gddBaseLabel('5', isCelsius)}</Box></SectionLabel>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.5 }}>
                Solar battery drain for warm (10) and cool (5) season crops
              </Typography>
              <Box sx={{ height: 250 }}>
                <GddChart fullscreen gddBase="5" tempUnit={tempUnit} gdd5Series={gdd5Series} gdd10Series={gdd10Series} lfd={lfd} ffd={ffd} />
              </Box>
            </Box>

            <Box>
              <SectionLabel>SEASON PROGRESS · <Box component="span" sx={{ fontWeight: 800 }}>{gddBaseLabel('10', isCelsius)}</Box></SectionLabel>
              <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.6rem', display: 'block', mb: 0.5 }}>
                Solar battery drain for warm (10) and cool (5) season crops
              </Typography>
              <Box sx={{ height: 250 }}>
                <GddChart fullscreen gddBase="10" tempUnit={tempUnit} gdd5Series={gdd5Series} gdd10Series={gdd10Series} lfd={lfd} ffd={ffd} />
              </Box>
            </Box>

            <CropHarvestTable crops={crops} minHeight={0} />

            <Box sx={{ height: 200, flexShrink: 0 }}>
              <TaskVelocityChart />
            </Box>

            <Box sx={{ height: 200, flexShrink: 0 }}>
              <CropPhaseChart />
            </Box>

            <NumericTotals flatList />
          </Stack>
        )}
      </Box>
    </Box>
  )
}

function MetricsFullscreen() {
  const { farmId, loading } = useFarmId()

  if (loading) return <LinearProgress />

  if (!farmId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a farm to view metrics.</Alert>
      </Box>
    )
  }

  return <MetricsFullscreenInner />
}

export default MetricsFullscreen
